import {
  OBRA_STATUS_LABELS,
  VISIT_STATUS_LABELS,
  type ObraStatus,
  type VisitStatus,
} from '@obras/shared';
import {
  CircleCheck,
  CircleDot,
  CircleSlash,
  Clock,
  PauseCircle,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '../../lib/cn.js';

export type Tone = 'muted' | 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'paused';

const TONES: Record<Tone, string> = {
  muted: 'bg-sunken text-ink-3',
  primary: 'bg-primary-soft text-primary-ink',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
  info: 'bg-info-soft text-info',
  paused: 'bg-paused-soft text-paused',
};

/**
 * Cheio: texto branco sobre a cor sólida do tom, igual nos dois temas. Nasceu
 * para pousar sobre foto — a foto não muda com o tema, e o selo nela também
 * não — e serve à contagem que pede atenção, como as notificações da lista. O
 * laranja é o `primary`; as outras cores são as semânticas do claro (`photo-*`),
 * escuras o bastante para o branco.
 */
const TONES_SOLID: Record<Tone, string> = {
  muted: 'bg-black/70',
  primary: 'bg-primary',
  success: 'bg-photo-success',
  warning: 'bg-photo-warning',
  danger: 'bg-photo-danger',
  info: 'bg-photo-info',
  paused: 'bg-photo-paused',
};

/**
 * Badge sempre carrega ícone + texto.
 * Cor nunca é o único portador da informação — design.md §3.
 *
 * Quadrado, com o fundo tênue do tom e um filete tracejado de 1px na cor do
 * próprio texto a 40% (`border-current/40`): o selo se lê como etiqueta
 * carimbada, não como botão, e o tracejado não pesa mais que o rótulo. A altura
 * é a de antes — o filete sai do respiro vertical.
 *
 * Cheio (`solid`) o selo é outro: sólido, na cor do tom, com texto e ícone
 * brancos em peso 600 e sem tracejado — sobre uma imagem qualquer, o tênue e o
 * tracejado sumiriam. Um fio mais alto (24px), porque é placa, não etiqueta.
 */
export function Badge({
  tone = 'muted',
  icon: Icon,
  solid = false,
  children,
  className,
}: {
  tone?: Tone;
  icon?: LucideIcon;
  /** Selo cheio: sobre foto, como no cartão de obra, ou contagem que pede atenção. */
  solid?: boolean | undefined;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-[12px] whitespace-nowrap',
        solid
          ? cn('px-2.5 py-1 leading-4 font-semibold text-white', TONES_SOLID[tone])
          : cn('border border-dashed border-current/40 px-2 py-px font-medium', TONES[tone]),
        className,
      )}
    >
      {Icon ? <Icon size={13} strokeWidth={1.75} className="shrink-0" aria-hidden /> : null}
      {children}
    </span>
  );
}

const OBRA_STATUS_STYLE: Record<ObraStatus, { tone: Tone; icon: LucideIcon }> = {
  em_andamento: { tone: 'primary', icon: CircleDot },
  paralisada: { tone: 'paused', icon: PauseCircle },
  concluida: { tone: 'success', icon: CircleCheck },
  cancelada: { tone: 'danger', icon: CircleSlash },
};

export function ObraStatusBadge({
  status,
  solid,
}: {
  status: ObraStatus;
  solid?: boolean | undefined;
}) {
  const style = OBRA_STATUS_STYLE[status];
  return (
    <Badge tone={style.tone} icon={style.icon} solid={solid}>
      {OBRA_STATUS_LABELS[status]}
    </Badge>
  );
}

const VISIT_STATUS_STYLE: Record<VisitStatus, { tone: Tone; icon: LucideIcon }> = {
  em_dia: { tone: 'success', icon: CircleCheck },
  vencendo: { tone: 'warning', icon: Clock },
  vencida: { tone: 'danger', icon: TriangleAlert },
  sem_visita: { tone: 'muted', icon: CircleDot },
  // Obra encerrada não tem próxima visita; o selo informa, não alerta.
  nao_se_aplica: { tone: 'muted', icon: CircleSlash },
};

export function VisitStatusBadge({
  status,
  solid,
}: {
  status: VisitStatus;
  solid?: boolean | undefined;
}) {
  const style = VISIT_STATUS_STYLE[status];
  return (
    <Badge tone={style.tone} icon={style.icon} solid={solid}>
      {VISIT_STATUS_LABELS[status]}
    </Badge>
  );
}
